import React, {useCallback, useEffect, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import TryAgain from "../../../../../../components/app-specific/try-again";
import ProductFeedbackCard from "../../../../../../components/app-specific/product-cards/product-feedback-card";
import Utils from "../../../../../../../core/services/utils/utils";
import ElementIds from "../../../../../../../core/models/element-ids";

const LookupProductInformationViewFeedbacksSection = ({className, idToListenTo, id, partNumber, removeSection, addSection, shown}) => {
    const isMounted = useIsMounted();
    const [loading, setLoading] = useState(true);
    const [feedbacks, setFeedbacks] = useState([]);
    const scrollerRef = useRef(false);

    /**
     * Fetches the part feedbacks form the server. only sets the data when component is still mounted, and api
     * result is successful.
     */
    const getPartFeedbacks = useCallback(async () => {
        scrollerRef.current = true;
        setLoading(true);
        const response = await Api.getPartFeedbacks(partNumber);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFeedbacks(response.data?.map(e => ({
                ...e,
                loading: false,
                children: e.children?.map(child => ({...child, loading: false}))
            })) ?? []);
        }
        if (!response?.resultFlag || !response?.data?.length) {
            return removeSection(id);
        }
        setLoading(false);
    }, [id, isMounted, partNumber, removeSection])

    /**
     * As soon as the component mounts:
     * - attaches an event listener to window's scrolling effect that will determine if the extra information of the
     * a part needs to be fetched or not.
     */
    useEffect(() => {
        addSection(id);
        const element = document.getElementById(idToListenTo);
        if (Utils.isScrolledIntoView(element, true)) {
            void getPartFeedbacks();
        } else {
            scrollerRef.current = false;
            const onContainerScrolled = () => {
                if (scrollerRef.current)
                    return;
                const element = document.getElementById(idToListenTo);
                if (!element)
                    return;
                if (!Utils.isScrolledIntoView(element))
                    return;
                void getPartFeedbacks();
            }
            const main = document.getElementById(ElementIds.panel.main);
            main.addEventListener('scroll', onContainerScrolled, {passive: true})
            return () => window.removeEventListener('scroll', onContainerScrolled)
        }
    }, [addSection, getPartFeedbacks, id, idToListenTo])

    /**
     * Removes a feedback by calling the server.
     *
     * If the feedback has a parent, then remove the feedback with all of its children,
     * if feedback is a child with no children, remove it.
     * @param feedback {any}
     * @param parent {any}
     */
    const onRemove = (feedback, parent = null) => {
        if (parent) {
            //Comment has a parent
            setFeedbacks(prevState => prevState.map(e => e.id === parent.id ? {
                ...e,
                children: e.children?.map(c => c.id === feedback.id ? {...c, loading: true} : c),
            } : e))
            Api.removeFeedback(feedback.id).then((response) => {
                if (!isMounted()) return;
                // api result false, make the loading false
                if (!response?.resultFlag) {
                    setFeedbacks(prevState => prevState.map(e => e.id === parent.id ? {
                        ...e,
                        children: e.children?.map(c => c.id === feedback.id ? {...c, loading: false} : c),
                    } : e))
                    return;
                }
                setFeedbacks(prevState => prevState.map(e => e.id === parent.id ? {
                    ...e,
                    children: e.children?.filter(c => c.id !== feedback.id)
                } : e))
            })
            return;
        }
        //Comment does not have a parent
        setFeedbacks(prevState => prevState.map(e => e.id === feedback.id ? {...e, loading: true} : e))
        Api.removeFeedback(feedback.id).then((response) => {
            if (!isMounted()) return;
            // api result false, make the loading false
            if (!response?.resultFlag) {
                setFeedbacks(prevState => prevState.map(e => e.id === feedback.id ? {...e, loading: false} : e))
                return;
            }
            setFeedbacks(prevState => prevState.filter(e => e.id !== feedback.id))
        })
    }

    /**
     * Tiggels the visibility of a feedback by calling the server api.
     * @param feedback {any}
     * @param parent {any}
     */
    const toggleFeedbackVisibility = (feedback, parent = null) => {
        if (parent) {
            //feedback has a parent
            setFeedbacks(prevState => prevState.map(e => e.id === parent.id ? {
                ...e,
                children: e.children?.map(c => c.id === feedback.id ? {...c, loading: true} : c),
            } : e))
            Api.toggleFeedbackVisibility(feedback.id).then((response) => {
                if (!isMounted()) return;
                // api result false, make loading false
                if (!response?.resultFlag) {
                    setFeedbacks(prevState => prevState.map(e => e.id === parent.id ? {
                        ...e,
                        children: e.children?.map(c => c.id === feedback.id ? {...c, loading: false} : c),
                    } : e))
                    return;
                }
                const prevIsAvailable = feedbacks.find(e => e.id === parent.id)?.children?.find(c => c.id === feedback.id)?.isAvailable;
                setFeedbacks(prevState => prevState.map(e => e.id === parent.id ? {
                    ...e,
                    children: e.children?.map(c => c.id === feedback.id ? {
                        ...c,
                        isAvailable: !prevIsAvailable,
                        loading: false
                    } : c)
                } : e))
            })
            return;
        }
        //feedback does not have a parent
        setFeedbacks(prevState => prevState.map(e => e.id === feedback.id ? {...e, loading: true} : e))
        Api.toggleFeedbackVisibility(feedback.id).then((response) => {
            if (!isMounted()) return;
            // api result false, make loading false
            if (!response || !response.resultFlag) {
                setFeedbacks(prevState => prevState.map(e => e.id === feedback.id ? {...e, loading: false} : e))
                return;
            }
            setFeedbacks(prevState => prevState.map(e => e.id === feedback.id ? {
                ...e,
                isAvailable: !feedback.isAvailable,
                loading: false,
            } : e))
        })
    }

    if (!shown) {
        return (
            <Row id={id} className={`no-gutters align-items-start justify-content-start w-100`}/>
        )
    }

    return (
        <Row id={id} className={`${className} no-gutters align-items-start justify-content-start w-100`}>
            {
                loading
                    ? (
                        <div id={id} className={`loading part-comment px-2 ${className}`}>
                            <div/>
                            <div/>
                            <div/>
                        </div>
                    )
                    : !feedbacks.length
                        ? (<TryAgain onClick={getPartFeedbacks} text={'No Review has been submitted for this part'}
                                     buttonText={'Try Again'}/>)
                        : <Row className='no-gutters w-100 align-items-start justify-content-center mx-2'>
                            <Col xs={12} className={'mb-4'}>
                                <p className='text-lg font-weight-semi-bold text-color-1'>Reviews</p>
                            </Col>
                            {
                                feedbacks?.map(feedback => (
                                    <Col key={feedback.id} xs={12}>
                                        <ProductFeedbackCard
                                            feedback={feedback}
                                            remove={onRemove}
                                            toggleVisibility={toggleFeedbackVisibility}
                                        />
                                    </Col>
                                ))
                            }
                        </Row>
            }
        </Row>
    );

}

export default LookupProductInformationViewFeedbacksSection;
